import type { SortMethod } from "@/__generated__/graphql";
import type { defaultConfig } from "@/scripts/settings";
import type {
  Cause,
  Effect,
  routeType,
  StatusType,
} from "../prisma/generated/type-graphql";

export enum ConnectionStatus {
  CONNECTED = "CONNECTED",
  CONNECTING = "CONNECTING",
  DISCONNECTED = "DISCONNECTED",
  DISABLED = "DISABLED",
}
type simpleKeys = [
  "home",
  "title",
  "search",
  "stop",
  "adress",
  "departureStop",
  "arrivalStop",
  "time",
  "minimumTransferTime",
  "pageNotFound",
  "tripNotFound",
  "stopNotFound",
  "favouriteStops",
  "fillAllFields",
  "noRoutesFound",
  "tryDisablingFilters",
  "disabledMap",
  "mapLoadError",
  "settings",
  "description",
  "noInformation",
  "loadingMap",
  "departure",
  "arrival",
  "booleans",
  "textboxes",
  "numbers",
  "selects",
  "source",
  "reset",
  "settings_reload",
  "otherTrips",
  "graphql",
  "extraOptions",
  "inVehicleTransfer",
  "errorHeader",
  "reservationRequired",
  "hideCancelled",
  "copyText",
  "copyUrl",
  "removePositionInVehicle",
  "occupancyIndicator",
  "occupancyEstimate",
  "import9292",
];
export type language = Record<simpleKeys[number], string> & {
  routeTypes: Record<routeType, string>;
  tripTitle: (trip: {
    route: {
      route_type: keyof typeof routeType;
      route_short_name: string;
    };
    trip_headsign: string;
  }) => string;
  receivedOn: (time: number | null | undefined) => string;
  stopTitle: ({ stop_name }: { stop_name: string }) => string;
  plannerTitle: ({
    fromStopName,
    toStopName,
  }: {
    fromStopName: string;
    toStopName: string;
  }) => string;
  statusText: { [key in keyof typeof StatusType]: (stop?: string) => string };
  connectionStatus: Record<ConnectionStatus, string>;
  settingItems: {
    [key in keyof typeof defaultConfig]: string;
  };
  wrongPlatformMightBe: (platform: string) => string;
  cause: {
    [key in Cause]: string;
  } & {
    NULL: string;
  };
  effect: {
    [key in Effect]: string;
  } & {
    NULL: string;
  };
  alertSubtitle: (cause: string, effect: string) => string;
  sortMethods: Record<
    SortMethod,
    {
      title: string;
      subtitle: string;
      short: string;
    }
  >;
};
const nl: language = {
  routeTypes: {
    TRAM: "Tram",
    SUBWAY: "Metro",
    TRAIN: "Trein",
    BUS: "Bus",
    FERRY: "Veerpont",
    FUNICULAR: "Funicular",
  },
  home: "Home",
  title: "Reisplanner",
  search: "Zoeken",
  stop: "Halte",
  adress: "Adres",
  departureStop: "Vertrekhalte",
  arrivalStop: "Aankomsthalte",
  time: "Tijd",
  minimumTransferTime: "Minimale overstaptijd",
  pageNotFound: "Pagina niet gevonden",
  tripNotFound: "Rit niet gevonden",
  stopNotFound: "Halte niet gevonden",
  favouriteStops: "Favoriete haltes",
  fillAllFields: "Vul alle velden in",
  noRoutesFound: "Geen routes gevonden",
  tryDisablingFilters: "Geen routes gevonden. Probeer filters uit te zetten",
  disabledMap: "Kaart is uitgeschakeld",
  mapLoadError: "Kaart laden mislukt",
  settings: "Instellingen",
  description: "Reisplanner voor het openbaar vervoer in Nederland",
  tripTitle: (trip) =>
    `${nl.routeTypes[trip.route.route_type]} ${trip.route.route_short_name} richting ${trip.trip_headsign}`,
  receivedOn: (time) =>
    time
      ? `Ontvangen op ${new Date(time).toLocaleString()}`
      : "Geen data ontvangen",
  stopTitle: ({ stop_name }) => `Vertrektijden van ${stop_name}`,
  plannerTitle: ({ fromStopName, toStopName }) =>
    `Reis van ${fromStopName} naar ${toStopName}`,
  statusText: {
    FARAWAY: (stop?: string) => (stop ? `Ver weg van ${stop}` : "Ver weg"),
    ONROUTE: (stop?: string) => (stop ? `Onderweg naar ${stop}` : "Onderweg"),
    ARRIVAL: (stop?: string) =>
      stop ? `Bijna bij ${stop}` : "Bijna bij halte",
    ONSTOP: (stop?: string) => (stop ? `Aangekomen op ${stop}` : "Aangekomen"),
    DEPARTURE: (stop?: string) =>
      stop ? `Vertrokken van ${stop}` : "Vertrokken",
  },
  noInformation: "Geen informatie",
  loadingMap: "Kaart laden",
  departure: "Vertrek",
  arrival: "Aankomst",
  connectionStatus: {
    CONNECTED: "Verbonden",
    CONNECTING: "Verbinden...",
    DISCONNECTED: "Verbinding verbroken",
    DISABLED: "",
  },
  settingItems: {
    syncBetweenTabs: "Sync settings tussen tabbladen",
    showReceivedTime: "Ontvangen tijd weergeven",
    showOtherTrips: "Andere ritten van voertuigen weergeven",
    defaultMinimumTransferTime: "Standaard minimale overstaptijd",
    darkMode: "Donkere modus",
    hideStopLabels: "Halte labels verbergen",
    mapStyle: "Kaartstijl",
    language: "Taal",
    otherStopsAutoExpand: "Toon andere haltes in planner automatisch",
    shareHref: "Deel links in markdown format",
    shareFormatting: "Deel opmaak in markdown format",
    showTripDate: "Toon ritdatum",
    alertsEnabled: "Verstoringen weergeven (BETA)",
    debugReversed: "Debug rijrichting van treinen",
    showAgency: "Laat vervoerder zien bij de rit",
  },
  booleans: "Ja/Nee",
  textboxes: "Tekst",
  numbers: "Nummer",
  selects: "Selecteervakjes",
  source: "Broncode",
  reset: "Reset",
  settings_reload: "Opslaan",
  otherTrips: "Andere ritten van voertuigen:",
  graphql: "GraphQL Schema",
  extraOptions: "Meer overstappen(traag!)",
  wrongPlatformMightBe: (platform: string) =>
    `Mogelijk verkeerde spoor, kan ${platform} zijn`,
  inVehicleTransfer: "blijven zitten",
  errorHeader: "Error bij laden van data. Klik hier voor meer informatie.",
  reservationRequired: "Reserveren verplicht",
  hideCancelled: "Verberg geannuleerde ritten",
  copyText: "Kopieer tekst",
  copyUrl: "Kopieer URL",
  cause: {
    OTHER_CAUSE: "een niet verder gedefinieerde reden",
    TECHNICAL_PROBLEM: "een technische storing",
    STRIKE: "een staking",
    DEMONSTRATION: "een demonstratie",
    ACCIDENT: "een ongeluk",
    HOLIDAY: "een feestdag",
    WEATHER: "het weer",
    MAINTENANCE: "onderhoudswerkzaamheden",
    CONSTRUCTION: "bouwactiviteiten",
    POLICE_ACTIVITY: "politie-activiteit",
    MEDICAL_EMERGENCY: "een medische noodsituatie",
    NULL: "een niet gespecificeerde reden",
  },
  effect: {
    NO_SERVICE: "niet worden gereden op deze halte",
    REDUCED_SERVICE: "met verminderde dienstregeling gereden worden",
    SIGNIFICANT_DELAYS: "met aanzienlijke vertragingen gereden worden",
    DETOUR: "een omleiding zijn",
    ADDITIONAL_SERVICE: "extra ritten worden gereden",
    MODIFIED_SERVICE: "met een gewijzigde dienstregeling worden gereden",
    OTHER_EFFECT: "niet verder gespecificeerde effecten zijn",
    STOP_MOVED: "een halte verplaatst zijn",
    NULL: "een onbekende verandering zijn",
  },
  alertSubtitle: (cause: string, effect: string) =>
    `Door ${cause} zal er ${effect}.`,
  sortMethods: {
    DEPARTURE: {
      title: "Vertrek na",
      subtitle: "Zo kort mogelijke reistijd, met zo vroeg mogelijke aankomst",
      short: "Vertrek na",
    },
    DEPART_ASAP: {
      title: "Vertrek zo snel mogelijk na",
      subtitle: "Kan extra reistijd hebben",
      short: "Vertrek ZSM na",
    },
    ARRIVAL: {
      title: "Aankomen voor",
      subtitle: "Zo kort mogelijke reistijd, met zo laat mogelijke vertrek",
      short: "Aankomen voor",
    },
  },
  removePositionInVehicle: "Positie in voertuig weghalen",
  occupancyIndicator: "Drukte indicator",
  occupancyEstimate:
    "Dit is een berekening, aangeleverd bij het begin van de dag. Hierbij wordt geen rekening gehouden met vertragingen of rituitval.",
  import9292: "9292 importer",
};

const en: language = {
  routeTypes: {
    TRAM: "Tram",
    SUBWAY: "Metro",
    TRAIN: "Train",
    BUS: "Bus",
    FERRY: "Ferry",
    FUNICULAR: "Funicular",
  },
  home: "Home",
  title: "Journey planner",
  search: "Search",
  stop: "Stop",
  adress: "Address",
  departureStop: "Departure stop",
  arrivalStop: "Arrival stop",
  time: "Time",
  minimumTransferTime: "Minimum transfer time",
  pageNotFound: "Page not found",
  tripNotFound: "Trip not found",
  stopNotFound: "Stop not found",
  favouriteStops: "Favourite stops",
  fillAllFields: "Fill all fields",
  noRoutesFound: "No routes found",
  tryDisablingFilters: "No routes found. Try disabling filters",
  disabledMap: "Map is disabled",
  mapLoadError: "Map load failed",
  settings: "Settings",
  description: "Journey planner for public transport in the Netherlands",
  tripTitle: (trip) =>
    `${en.routeTypes[trip.route.route_type]} ${trip.route.route_short_name} in direction ${trip.trip_headsign}`,
  receivedOn: (time) =>
    time
      ? `Received on ${new Date(time).toLocaleString()}`
      : "No data received",
  stopTitle: ({ stop_name }) => `Departures from ${stop_name}`,
  plannerTitle: ({ fromStopName, toStopName }) =>
    `Journey from ${fromStopName} to ${toStopName}`,
  statusText: {
    FARAWAY: (stop?: string) => (stop ? `Far away from ${stop}` : "Far away"),
    ONROUTE: (stop?: string) => (stop ? `On the way to ${stop}` : "On the way"),
    ARRIVAL: (stop?: string) => (stop ? `Almost at ${stop}` : "Almost at stop"),
    ONSTOP: (stop?: string) => (stop ? `Arrived at ${stop}` : "Arrived"),
    DEPARTURE: (stop?: string) => (stop ? `Departed from ${stop}` : "Departed"),
  },
  noInformation: "No information",
  loadingMap: "Loading map",
  departure: "Departure",
  arrival: "Arrival",
  connectionStatus: {
    CONNECTED: "Connected",
    CONNECTING: "Connecting...",
    DISCONNECTED: "Connection lost",
    DISABLED: "",
  },
  settingItems: {
    syncBetweenTabs: "Sync settings between tabs",
    showReceivedTime: "show received time",
    showOtherTrips: "Show other trips of vehicles",
    defaultMinimumTransferTime: "Default minimum transfer time",
    darkMode: "Dark mode",
    hideStopLabels: "Hide stop labels",
    mapStyle: "Map style",
    language: "Language",
    otherStopsAutoExpand: "Show other stops in planner automatically",
    shareHref: "Share links in markdown format",
    shareFormatting: "Share formatting in markdown format",
    showTripDate: "Show trip date",
    alertsEnabled: "Show disruptions (BETA)",
    debugReversed: "Debug driving direction of trains",
    showAgency: "Show agency of trip",
  },
  booleans: "Yes/No",
  textboxes: "Text",
  numbers: "Number",
  selects: "Select boxes",
  source: "Source code",
  reset: "Reset",
  settings_reload: "Apply",
  otherTrips: "Other trips of vehicles:",
  graphql: "GraphQL Schema",
  extraOptions: "More transfers(slow!)",
  wrongPlatformMightBe: (platform: string) =>
    `Possible wrong platform, might be ${platform}`,
  inVehicleTransfer: "can stay in vehicle",
  errorHeader: "Error loading data. Click here for more info.",
  reservationRequired: "Reservation required",
  hideCancelled: "Hide cancelled trips",
  copyText: "Copy text",
  copyUrl: "Copy URL",
  cause: {
    OTHER_CAUSE: "an undefined reason",
    TECHNICAL_PROBLEM: "a technical problem",
    STRIKE: "a strike",
    DEMONSTRATION: "a demonstration",
    ACCIDENT: "an accident",
    HOLIDAY: "a holiday",
    WEATHER: "the weather",
    MAINTENANCE: "maintenance work",
    CONSTRUCTION: "construction activities",
    POLICE_ACTIVITY: "police activity",
    MEDICAL_EMERGENCY: "a medical emergency",
    NULL: "an unspecified reason",
  },
  effect: {
    NO_SERVICE: "not be service at this stop",
    REDUCED_SERVICE: "be reduced service at this stop",
    SIGNIFICANT_DELAYS: "be significant delays at this stop",
    DETOUR: "be a detour",
    ADDITIONAL_SERVICE: "be additional service",
    MODIFIED_SERVICE: "be modified service",
    OTHER_EFFECT: "be unspecified effects",
    STOP_MOVED: "be a stop moved",
    NULL: "be an unknown change",
  },
  alertSubtitle: (cause: string, effect: string) =>
    `Due to ${cause} there will ${effect}.`,
  sortMethods: {
    DEPARTURE: {
      title: "Depart after",
      subtitle: "Shortest travel time, with earliest arrival",
      short: "Depart after",
    },
    DEPART_ASAP: {
      title: "Depart as soon as possible after",
      subtitle: "May have extra travel time",
      short: "Depart ASAP after",
    },
    ARRIVAL: {
      title: "Arrival before",
      subtitle: "Shortest travel time, with latest departure",
      short: "Arrival before",
    },
  },
  removePositionInVehicle: "Remove position in vehicle",
  occupancyIndicator: "Crowd indicator",
  occupancyEstimate:
    "This is an estimate provided at the beginning of the day. This does not include the effect of disruptions or delays.",
  import9292: "Import 9292 url",
};

const empty: language = {
  routeTypes: {
    TRAM: "",
    SUBWAY: "",
    TRAIN: "",
    BUS: "",
    FERRY: "",
    FUNICULAR: "",
  },
  home: "",
  title: "",
  search: "",
  stop: "",
  adress: "",
  departureStop: "",
  arrivalStop: "",
  time: "",
  minimumTransferTime: "",
  pageNotFound: "",
  tripNotFound: "",
  stopNotFound: "",
  favouriteStops: "",
  fillAllFields: "",
  noRoutesFound: "",
  tryDisablingFilters: "",
  disabledMap: "",
  mapLoadError: "",
  settings: "",
  description: "",
  tripTitle: () => "",
  stopTitle: () => "",
  receivedOn: () => "",
  plannerTitle: () => "",
  statusText: {
    FARAWAY: () => "",
    ONROUTE: () => "",
    ARRIVAL: () => "",
    ONSTOP: () => "",
    DEPARTURE: () => "",
  },
  noInformation: "",
  loadingMap: "",
  departure: "",
  arrival: "",
  connectionStatus: {
    CONNECTED: "",
    CONNECTING: "",
    DISCONNECTED: "",
    DISABLED: "",
  },
  settingItems: {
    syncBetweenTabs: "",
    showReceivedTime: "",
    showOtherTrips: "",
    defaultMinimumTransferTime: "",
    darkMode: "",
    hideStopLabels: "",
    mapStyle: "",
    language: "",
    otherStopsAutoExpand: "",
    shareHref: "",
    shareFormatting: "",
    showTripDate: "",
    alertsEnabled: "",
    debugReversed: "",
    showAgency: "",
  },
  booleans: "",
  textboxes: "",
  numbers: "",
  selects: "",
  source: "",
  reset: "",
  settings_reload: "",
  otherTrips: "",
  graphql: "",
  extraOptions: "",
  wrongPlatformMightBe: () => "",
  inVehicleTransfer: "",
  errorHeader: "",
  reservationRequired: "",
  hideCancelled: "",
  copyText: "",
  copyUrl: "",
  cause: {
    OTHER_CAUSE: "",
    TECHNICAL_PROBLEM: "",
    STRIKE: "",
    DEMONSTRATION: "",
    ACCIDENT: "",
    HOLIDAY: "",
    WEATHER: "",
    MAINTENANCE: "",
    CONSTRUCTION: "",
    POLICE_ACTIVITY: "",
    MEDICAL_EMERGENCY: "",
    NULL: "",
  },
  effect: {
    NO_SERVICE: "",
    REDUCED_SERVICE: "",
    SIGNIFICANT_DELAYS: "",
    DETOUR: "",
    ADDITIONAL_SERVICE: "",
    MODIFIED_SERVICE: "",
    OTHER_EFFECT: "",
    STOP_MOVED: "",
    NULL: "",
  },
  alertSubtitle: () => "",
  sortMethods: {
    DEPARTURE: {
      title: "",
      subtitle: "",
      short: "",
    },
    DEPART_ASAP: {
      title: "",
      subtitle: "",
      short: "",
    },
    ARRIVAL: {
      title: "",
      subtitle: "",
      short: "",
    },
  },
  removePositionInVehicle: "",
  occupancyIndicator: "",
  occupancyEstimate: "",
  import9292: "",
};

const languages = { nl, en, empty };
export default languages;
